import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  KeyboardEvent,
  useMemo,
} from "react";
import request from "@src/utils/request";
import classNames from "classnames";
import useClickedOutside from "@src/hooks/useClickedOutside";
import { ApiRoutes } from "@src/constants/ApiRoutes";
import styles from "./AutocompleteInput.module.scss";

interface Suggestion {
  id: string;
  label: string;
  value: string;
}

export interface AutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  onSearch?: (searchTerm?: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  inputStyle?: React.CSSProperties;
  width?: number | string;
  height?: number | string;
  limit?: number;
  enableSuggestions?: boolean;
  suggestionsEndpoint?: string;
  suggestionsQuery?: Record<string, any>;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  autoFocus?: boolean;
  "data-testid"?: string;
}

export default function AutocompleteInput({
  value,
  onChange,
  onSearch,
  placeholder = "Search products...",
  className,
  inputClassName,
  inputStyle,
  width,
  height,
  limit = 5,
  enableSuggestions = true,
  suggestionsEndpoint = "/api/v1/suggestions/",
  suggestionsQuery = {},
  disabled = false,
  required = false,
  label,
  autoFocus = false,
  "data-testid": dataTestId = "autocomplete-input",
}: AutocompleteInputProps) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [internalValue, setInternalValue] = useState(value);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(
    null,
  ) as React.RefObject<HTMLElement>;
  const suggestionsQueryKey = useMemo(
    () => JSON.stringify(suggestionsQuery || {}),
    [suggestionsQuery],
  );
  const stableSuggestionsQuery = useMemo(
    () => JSON.parse(suggestionsQueryKey),
    [suggestionsQueryKey],
  );

  // Close dropdown when clicking outside
  useClickedOutside(containerRef, () => setIsOpen(false));

  // Sync internal value with external value
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Fetch suggestions when internal value changes
  useEffect(() => {
    if (!enableSuggestions) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }
    if (internalValue.length >= 2) {
      request()
        .get(suggestionsEndpoint)
        .query({ ...stableSuggestionsQuery, q: internalValue, limit })
        .end((err: any, res: any) => {
          if (err) {
            console.error("Error fetching suggestions:", err);
            setSuggestions([]);
            return;
          }

          const suggestionsData = res?.body?.suggestions || [];
          const formattedSuggestions: Suggestion[] = suggestionsData.map(
            (suggestion: string, index: number) => ({
              id: `suggestion-${index}`,
              label: suggestion,
              value: suggestion,
            }),
          );

          setSuggestions(formattedSuggestions);
          if (hasUserInteracted) {
            setIsOpen(formattedSuggestions.length > 0);
          }
        });
    } else {
      setSuggestions([]);
      setIsOpen(false);
    }
  }, [
    internalValue,
    limit,
    hasUserInteracted,
    enableSuggestions,
    suggestionsEndpoint,
    suggestionsQueryKey,
    stableSuggestionsQuery,
  ]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInternalValue(newValue);
      onChange(newValue);
      setHighlighted(-1);
      setHasUserInteracted(true);
    },
    [onChange],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Don't interfere with left/right arrow keys - let them work normally
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      return; // Let the browser handle cursor movement
    }

    // Handle Enter key regardless of dropdown state
    if (e.key === "Enter") {
      e.preventDefault();
      if (isOpen && highlighted >= 0 && suggestions.length > 0) {
        handleSelect(suggestions[highlighted]);
      } else {
        setIsOpen(false);
        setHighlighted(-1);
        onSearch?.(internalValue);
      }
      return;
    }

    // Handle Escape to close dropdown
    if (e.key === "Escape" && isOpen) {
      setIsOpen(false);
      setHighlighted(-1);
      return;
    }

    // Only handle up/down arrow keys for dropdown navigation when dropdown is open AND has suggestions
    if (isOpen && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlighted((prev) => Math.min(prev + 1, suggestions.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlighted((prev) => Math.max(prev - 1, 0));
      }
    }
  };

  const handleSelect = (suggestion: Suggestion) => {
    // Update the value first
    onChange(suggestion.value);
    setInternalValue(suggestion.value);
    setIsOpen(false);
    setHighlighted(-1);
    setHasUserInteracted(false); // Prevent dropdown from reopening after selection

    // Pass the selected value directly to search to avoid state timing issues
    onSearch?.(suggestion.value);
  };

  const handleFocus = () => {
    if (!enableSuggestions) {
      return;
    }
    setHasUserInteracted(true);
    if (suggestions.length > 0) {
      setIsOpen(true);
    }
  };

  return (
    <div
      ref={containerRef}
      className={classNames(styles.autocomplete, className)}
      style={{ width: width || "100%" }}
    >
      {label && (
        <label htmlFor="search-input" className={styles.label}>
          {label} {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div className={styles.inputWrapper}>
        <input
          ref={inputRef}
          id="search-input"
          type="text"
          value={internalValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          autoFocus={autoFocus}
          className={classNames(styles.input, inputClassName)}
          style={{ height, ...inputStyle }}
          data-testid={dataTestId}
        />
        <i
          className={classNames(
            "fa-light fa-magnifying-glass clickable",
            styles.searchIcon,
          )}
          onClick={() => onSearch?.(internalValue)}
          data-testid={`${dataTestId}-search-icon`}
        />
      </div>

      {isOpen && suggestions.length > 0 && (
        <ul className={styles.dropdown} data-testid={`${dataTestId}-dropdown`}>
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion.id}
              className={classNames(styles.item, {
                [styles.highlighted]: index === highlighted,
              })}
              onMouseEnter={() => setHighlighted(index)}
              onClick={() => handleSelect(suggestion)}
              data-testid={`${dataTestId}-suggestion-${index}`}
            >
              {suggestion.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
